import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuMoveDiagonal, LuMoveDiagonal2, LuMoveHorizontal, LuMoveVertical } from "react-icons/lu";
import AlertDialog from "../../../components/AlertDialog";
import { DIRECTION_DIAGONAL_LEFT_UP, DIRECTION_DIAGONAL_RIGHT_UP, DIRECTION_HORIZONTAL, DIRECTION_VERTICAL } from "../../../pzl_common/PuzzleUtil";
import DictionaryApiService from "../../../system/integrate/DictionaryApiService";
import GridCandEntry from "./GridCandEntry";
import AdminGridBoardService, { DRAFT } from "../AdminGridBoardService";
import Logger from "../../../../services/Logger";
import GirdCanAssocConfirmDialog from "./GirdCanAssocConfirmDialog";

import { generatePositions, getGridWord } from "./GridCandUtils";
import { resolveTplGridDims, resolveTplGridSize } from "../gridTplSize";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

// Normalize a single letter for comparison (French mode)
const normalizeLetter = (letter, language) => {
    if (!letter || typeof letter !== 'string') return letter;
    if (language === 'french') {
        return normalizeFrenchWord(letter);
    }
    return letter.toLowerCase();
};

export default function GridCandManual({
    onWordAdd,
    onWordSelect,
    selectedManualWord,
    onShowSnackbar,
    square,
    gridTpl,
    onRefresh
}) {
    const { rows: gridRowsCount, cols: gridColsCount } = resolveTplGridDims(gridTpl);
    const gridSize = resolveTplGridSize(gridTpl);
    const gridCellCount = gridRowsCount * gridColsCount;
    const [manualPattern, setManualPattern] = useState('');
    const [manualRow, setManualRow] = useState(0);
    const [manualCol, setManualCol] = useState(0);
    const [manualDirection, setManualDirection] = useState(DIRECTION_HORIZONTAL);
    const [leftToRight, setLeftToRight] = useState(true);
    const [manualCandidates, setManualCandidates] = useState([]);
    const [manualLoading, setManualLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const updatingWordRef = useRef(false);
    const updatingFormRef = useRef(false);
    const manualChangeRef = useRef(false);

    // Alert dialog state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    // Scattered placement dialog state
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [wordToPlace, setWordToPlace] = useState(null);
    const [wordToPlaceDisplay, setWordToPlaceDisplay] = useState(null); // Display form (e.g., "run on")

    // Helper function to get direction-specific label
    const getDirectionLabel = () => {
        if (manualDirection === 'scattered') {
            return "Scattered Placement";
        }
        switch (manualDirection) {
            case DIRECTION_HORIZONTAL:
                return leftToRight ? "Left to Right" : "Right to Left";
            case DIRECTION_VERTICAL:
                return leftToRight ? "Top to Bottom" : "Bottom to Top";
            case DIRECTION_DIAGONAL_RIGHT_UP:
                return leftToRight ? "Top-Right to Bottom-Left" : "Bottom-Left to Top-Right";
            case DIRECTION_DIAGONAL_LEFT_UP:
                return leftToRight ? "Top-Left to Bottom-Right" : "Bottom-Right to Top-Left";
            default:
                return leftToRight ? "Forward" : "Reverse";
        }
    };

    // Helper function to map word characters to positions correctly
    const mapWordToPositions = (positions, word, direction, isReversed) => {
        const isDiagonal = direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP;
        const length = positions.length;
        
        for (let i = 0; i < length; i++) {
            let charIndex;
            if (isReversed && isDiagonal) {
                // For diagonal directions with isReversed=true:
                // Position i=0 is the start (bottom-left/right), should have word[0]
                charIndex = i;
            } else if (isReversed && !isDiagonal) {
                // For horizontal/vertical with isReversed=true:
                // Position i=0 is the leftmost/bottommost, should have word[length-1]
                charIndex = length - 1 - i;
            } else {
                // For non-reversed (all directions):
                // Position i=0 is the start, should have word[0]
                charIndex = i;
            }
            
            if (charIndex >= 0 && charIndex < word.length) {
                positions[i].value = word[charIndex].toLowerCase();
            }
        }
        
        return positions;
    };

    const handleManualPatternSearch = useCallback(async () => {
        if (!manualPattern.trim()) {
            onShowSnackbar("Please enter a pattern", "error");
            return;
        }
        
        if (manualPattern.trim().length <= 3) {
            onShowSnackbar("Pattern must be longer than 3 characters", "error");
            return;
        }

        setManualLoading(true);
        setManualCandidates([]);

        try {
            const candidates = await DictionaryApiService.wildcardSearch(manualPattern, gridTpl?.language || 'english');

            // Transform candidates to include position information for the selected direction
            const transformedCandidates = [];
            candidates.forEach(candidate => {
                // Keep original word with hyphens for display, remove hyphens for grid placement
                const wordWithHyphens = candidate.word; // Display version (e.g., "ex-voto", "run on")
                const gridWord = getGridWord(candidate.word); // Grid version without hyphens/spaces (e.g., "exvoto", "runon")
                
                // For scattered placement, we don't need to generate positions here
                // Positions will be generated when the word is actually placed
                if (manualDirection === 'scattered') {
                    transformedCandidates.push({
                        word: wordWithHyphens, // Display with hyphens
                        gridWord: gridWord, // Grid version without hyphens
                        score: candidate.score || 0,
                        startRow: 0, // Not used for scattered
                        startCol: 0, // Not used for scattered
                        direction: 'scattered',
                        positions: [], // Will be generated on placement
                        isReversed: false
                    });
                } else {
                    // Add normal word (left-to-right/top-to-bottom)
                    if (leftToRight) {
                        transformedCandidates.push({
                            word: wordWithHyphens, // Display with hyphens
                            gridWord: gridWord, // Grid version without hyphens
                            score: candidate.score || 0,
                            startRow: manualRow,
                            startCol: manualCol,
                            direction: manualDirection,
                            positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, false),
                            isReversed: false
                        });
                    }
                    // Add reversed word (right-to-left/bottom-to-top) - only if leftToRight is disabled
                    if (!leftToRight) {
                        let positions = generatePositions(manualRow, manualCol, manualDirection, gridWord.length, "", true);
                        mapWordToPositions(positions, gridWord, manualDirection, true);
                        
                        transformedCandidates.push({
                            word: wordWithHyphens, // Display with hyphens
                            gridWord: gridWord, // Grid version without hyphens
                            score: candidate.score || 0,
                            startRow: manualRow,
                            startCol: manualCol,
                            direction: manualDirection,
                            positions: positions,
                            isReversed: true
                        });
                    }
                }
            });

            setManualCandidates(transformedCandidates);

            if (transformedCandidates.length === 0) {
                onShowSnackbar("No word candidates found for this pattern", "info");
            }
        } catch (error) {
            Logger.error("Error searching for manual candidates:", error);
            onShowSnackbar("Error searching for words. Please try again.", "error");
        } finally {
            setManualLoading(false);
        }
    }, [manualPattern, manualRow, manualCol, manualDirection, leftToRight, onShowSnackbar]);

    // Memoize transformed candidates to prevent unnecessary re-renders
    // Update positions when manualRow or manualCol change (e.g., from cell clicks)
    const memoizedCandidates = useMemo(() => {
        if (!manualCandidates || manualCandidates.length === 0) {
            return [];
        }
        
        return manualCandidates
            .map(candidate => {
                // For scattered placement, positions are empty and will be generated on placement
                if (candidate.direction === 'scattered') {
                    return {
                        ...candidate,
                        key: `${candidate.word}-scattered`
                    };
                }
                
                // Recalculate positions based on current manualRow and manualCol
                const gridWord = candidate.gridWord || getGridWord(candidate.word);
                const updatedPositions = generatePositions(
                    manualRow,
                    manualCol,
                    candidate.direction,
                    gridWord.length,
                    "",
                    candidate.isReversed
                );
                mapWordToPositions(updatedPositions, gridWord, candidate.direction, candidate.isReversed);
                
                return {
                    ...candidate,
                    startRow: manualRow,
                    startCol: manualCol,
                    positions: updatedPositions,
                    key: `${candidate.word}-${manualRow}-${manualCol}-${candidate.direction}-${candidate.isReversed}`
                };
            })
            .sort((a, b) => (b.score || 0) - (a.score || 0)); // Sort by score descending
    }, [manualCandidates, manualRow, manualCol]);

    // Calculate actual filled cells by considering unique coordinates
    const calculateFilledCells = useCallback(() => {
        if (!gridTpl?.filledCoordinates) return 0;
        try {
            const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
            const uniqueCells = new Set();

            filledCoordinates.forEach(word => {
                if (word.text && word.positions) {
                    // Add each position coordinate to the set
                    word.positions.forEach(pos => {
                        uniqueCells.add(`${pos.row}-${pos.col}`);
                    });
                }
            });

            return uniqueCells.size;
        } catch (error) {
            Logger.error("Error calculating filled cells:", error);
            return 0;
        }
    }, [gridTpl?.filledCoordinates]);

    const emptyCellsCount = gridCellCount - calculateFilledCells();

    // Pagination constants
    const CANDIDATES_PER_PAGE = 10;
    const totalPages = Math.ceil(memoizedCandidates.length / CANDIDATES_PER_PAGE);
    const startIndex = (currentPage - 1) * CANDIDATES_PER_PAGE;
    const endIndex = startIndex + CANDIDATES_PER_PAGE;
    const paginatedCandidates = memoizedCandidates.slice(startIndex, endIndex);

    // Reset to page 1 when candidates change
    useEffect(() => {
        setCurrentPage(1);
    }, [memoizedCandidates.length]);

    // Pagination handlers
    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    // Auto-search when pattern changes
    useEffect(() => {
        // Skip auto-search if form fields are being updated from a word selection
        if (updatingFormRef.current) {
            return;
        }

        const timeoutId = setTimeout(() => {
            // Double-check the flag in case it changed during the timeout
            if (!updatingFormRef.current && manualPattern.trim().length >= 2) {
                handleManualPatternSearch();
            } else if (!updatingFormRef.current) {
                setManualCandidates([]);
            }
        }, 800); // Increased debounce to 800ms to reduce flickering

        return () => clearTimeout(timeoutId);
    }, [manualPattern, manualRow, manualCol, manualDirection, leftToRight, handleManualPatternSearch]);


    // Update form fields when a new word is selected or position is updated externally
    useEffect(() => {
        // Skip if this is a manual change (user changed form fields directly)
        if (manualChangeRef.current) {
            manualChangeRef.current = false;
            return;
        }
        
        if (selectedManualWord && !updatingWordRef.current) {
            updatingFormRef.current = true;
            const newRow = selectedManualWord.startRow || 0;
            const newCol = selectedManualWord.startCol || 0;
            const rowChanged = newRow !== manualRow;
            const colChanged = newCol !== manualCol;
            
            // Always update form fields to match the selected word's current state
            setManualRow(newRow);
            setManualCol(newCol);
            setManualDirection(selectedManualWord.direction || DIRECTION_HORIZONTAL);
            setLeftToRight(!selectedManualWord.isReversed);
            
            // If row or col changed and we have a pattern, refresh candidates (like clicking RefreshIcon)
            if ((rowChanged || colChanged) && manualPattern.trim().length >= 2) {
                // Trigger a refresh of candidates with new position
                setTimeout(() => {
                    handleManualPatternSearch();
                }, 100);
            }
            
            // Reset the flag after a delay longer than the auto-search debounce to prevent refresh
            setTimeout(() => {
                updatingFormRef.current = false;
            }, 1000);
        }
    }, [selectedManualWord, manualRow, manualCol, manualPattern, handleManualPatternSearch]);

    // Handle manual changes to form fields
    const handleManualRowChange = (value) => {
        const newRow = Math.max(0, Math.min(gridSize - 1, parseInt(value) || 0));
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setManualRow(newRow);
        if (selectedManualWord) {
            const gridWord = selectedManualWord.gridWord || getGridWord(selectedManualWord.word);
            // Use selectedManualWord's current startCol as source of truth, or fallback to manualCol state
            const currentCol = selectedManualWord.startCol !== undefined ? selectedManualWord.startCol : manualCol;
            let positions = generatePositions(newRow, currentCol, manualDirection, gridWord.length, "", selectedManualWord.isReversed);
            mapWordToPositions(positions, gridWord, manualDirection, selectedManualWord.isReversed);
            const updatedWord = {
                ...selectedManualWord,
                startRow: newRow,
                positions: positions
            };
            onWordSelect(updatedWord);
        }
    };

    const handleManualColChange = (value) => {
        const newCol = Math.max(0, Math.min(gridSize - 1, parseInt(value) || 0));
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setManualCol(newCol);
        if (selectedManualWord) {
            const gridWord = selectedManualWord.gridWord || getGridWord(selectedManualWord.word);
            // Use selectedManualWord's current startRow as source of truth, or fallback to manualRow state
            const currentRow = selectedManualWord.startRow !== undefined ? selectedManualWord.startRow : manualRow;
            let positions = generatePositions(currentRow, newCol, manualDirection, gridWord.length, "", selectedManualWord.isReversed);
            mapWordToPositions(positions, gridWord, manualDirection, selectedManualWord.isReversed);
            const updatedWord = {
                ...selectedManualWord,
                startCol: newCol,
                positions: positions
            };
            onWordSelect(updatedWord);
        }
    };

    const handleDirectionChange = (newDirection) => {
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setManualDirection(newDirection);
        if (selectedManualWord && newDirection !== 'scattered') {
            const gridWord = selectedManualWord.gridWord || getGridWord(selectedManualWord.word);
            let positions = generatePositions(manualRow, manualCol, newDirection, gridWord.length, "", selectedManualWord.isReversed);
            mapWordToPositions(positions, gridWord, newDirection, selectedManualWord.isReversed);
            const updatedWord = {
                ...selectedManualWord,
                direction: newDirection,
                positions: positions
            };
            onWordSelect(updatedWord);
        } else if (selectedManualWord && newDirection === 'scattered') {
            // For scattered, clear the selected word since positions can't be generated
            onWordSelect(null);
        }
    };

    const handleLeftToRightChange = (newLeftToRight) => {
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setLeftToRight(newLeftToRight);
        if (selectedManualWord) {
            const isReversed = !newLeftToRight;
            const gridWord = selectedManualWord.gridWord || getGridWord(selectedManualWord.word);
            let positions = generatePositions(manualRow, manualCol, manualDirection, gridWord.length, "", isReversed);
            mapWordToPositions(positions, gridWord, manualDirection, isReversed);
            const updatedWord = {
                ...selectedManualWord,
                isReversed: isReversed,
                positions: positions
            };
            onWordSelect(updatedWord);
        }
    };

    // Verify if the word can be placed at the specified position
    const verifyWordPlacement = (candidate) => {
        if (!square || square.length === 0) {
            return { isValid: false, message: "Puzzle grid is not available" };
        }

        // Use gridWord (without hyphens) for grid operations, but word (with hyphens) for display
        const gridWord = (candidate.gridWord || getGridWord(candidate.word)).toUpperCase();
        const displayWord = candidate.word; // Keep original with hyphens for error messages
        const positions = candidate.positions;
        const isReversed = candidate.isReversed || false;

        // Check if all positions are within bounds
        for (const pos of positions) {
            if (pos.row < 0 || pos.row >= square.length || pos.col < 0 || pos.col >= square[0].length) {
                return { 
                    isValid: false, 
                    message: `Word "${displayWord}" extends beyond puzzle boundaries at position (${pos.row}, ${pos.col})` 
                };
            }
        }

        // Check if the word matches existing letters in the puzzle
        const language = gridTpl?.language || 'english';
        for (let i = 0; i < positions.length; i++) {
            const pos = positions[i];
            const existingLetter = square[pos.row][pos.col];
            
            // The positions array should have the correct letter in pos.value for each position
            // Use pos.value if available (it's already correctly set by mapWordToPositions)
            // Otherwise fall back to calculating it based on direction and isReversed
            let expectedLetter;
            if (pos.value !== undefined && pos.value !== "") {
                // Use the value from the position (which was set correctly by mapWordToPositions)
                expectedLetter = pos.value.toUpperCase();
            } else {
                // Fallback: calculate expected letter based on direction and isReversed
                const isDiagonal = candidate.direction === DIRECTION_DIAGONAL_RIGHT_UP || candidate.direction === DIRECTION_DIAGONAL_LEFT_UP;
                let charIndex;
                if (isReversed && isDiagonal) {
                    // For diagonal with isReversed: position i should have word[i]
                    charIndex = i;
                } else if (isReversed && !isDiagonal) {
                    // For horizontal/vertical with isReversed: position i should have word[length-1-i]
                    charIndex = gridWord.length - 1 - i;
                } else {
                    // For non-reversed: position i should have word[i]
                    charIndex = i;
                }
                expectedLetter = (charIndex >= 0 && charIndex < gridWord.length) ? gridWord[charIndex] : "";
            }
            
            // If there's an existing letter, it must match the expected letter for this position
            // Normalize both letters for comparison when language is French
            if (existingLetter && existingLetter !== "") {
                const normalizedExisting = normalizeLetter(existingLetter, language);
                const normalizedExpected = normalizeLetter(expectedLetter, language);
                
                if (normalizedExisting !== normalizedExpected) {
                    return { 
                        isValid: false, 
                        message: `Word "${displayWord}" conflicts with existing letter "${existingLetter}" at position (${pos.row}, ${pos.col}). Expected "${expectedLetter}"` 
                    };
                }
            }
        }

        return { isValid: true, message: "Word can be placed successfully" };
    };

    const handleManualWordAdd = (candidate) => {
        // For scattered placement, open confirmation dialog
        if (candidate.direction === 'scattered') {
            setWordToPlace(candidate.gridWord || candidate.word);
            setWordToPlaceDisplay(candidate.word || candidate.gridWord);
            setConfirmDialogOpen(true);
            return;
        }

        // Verify word placement before adding
        const verification = verifyWordPlacement(candidate);
        
        if (!verification.isValid) {
            // Show alert dialog with error message
            setAlertMessage(verification.message);
            setAlertOpen(true);
            return;
        }

        onWordAdd(candidate);
        // Reset form and candidates after successful add
        setManualPattern('');
        setManualRow(0);
        setManualCol(0);
        setManualDirection(DIRECTION_HORIZONTAL);
        setLeftToRight(true);
        setManualCandidates([]);
    };

    // Handle confirmation and place word in puzzle (scattered placement)
    const handleConfirmWordPlacement = async (updatedTitle) => {
        if (!wordToPlace) return;

        try {
            // Normalize for grid placement: remove hyphens/spaces, then apply language-specific normalization
            const language = gridTpl?.language || 'english';
            let normalizedWord = getGridWord(wordToPlace);
            if (language === 'french') {
                normalizedWord = normalizeFrenchWord(normalizedWord);
            } else {
                normalizedWord = normalizedWord.toLowerCase();
            }

            const wordLength = normalizedWord.length;
            const positions = [];

            // Get current filled positions to avoid conflicts
            const currentFilledPositions = new Set();

            // Check the actual puzzle grid for filled cells
            if (square) {
                for (let row = 0; row < gridSize; row++) {
                    for (let col = 0; col < gridSize; col++) {
                        const char = square[row][col];
                        if (char && char !== "") {
                            currentFilledPositions.add(`${row}-${col}`);
                        }
                    }
                }
            }

            // Also check filledCoordinates from database
            if (gridTpl?.filledCoordinates) {
                try {
                    const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                    filledCoordinates.forEach(w => {
                        if (w.positions) {
                            w.positions.forEach(pos => {
                                if (pos.value) {
                                    const posKey = `${pos.row}-${pos.col}`;
                                    currentFilledPositions.add(posKey);
                                }
                            });
                        }
                    });
                } catch (error) {
                    Logger.error("Error parsing filled coordinates:", error);
                }
            }

            // Count total empty cells
            let totalEmptyCells = 0;
            for (let row = 0; row < gridSize; row++) {
                for (let col = 0; col < gridSize; col++) {
                    const positionKey = `${row}-${col}`;
                    if (!currentFilledPositions.has(positionKey)) {
                        totalEmptyCells++;
                    }
                }
            }

            // First, check if we have enough empty cells
            if (totalEmptyCells < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${totalEmptyCells} empty cells, need ${wordLength}`);
            }

            // Place characters from top-left to bottom-right in empty cells
            let charIndex = 0;
            for (let row = 0; row < gridSize && charIndex < wordLength; row++) {
                for (let col = 0; col < gridSize && charIndex < wordLength; col++) {
                    const positionKey = `${row}-${col}`;

                    // Check if this position is empty
                    if (!currentFilledPositions.has(positionKey)) {
                        positions.push({
                            row: row,
                            col: col,
                            value: normalizedWord[charIndex]
                        });
                        // Add to currentFilledPositions to prevent reuse
                        currentFilledPositions.add(positionKey);
                        charIndex++;
                    }
                }
            }

            // Make sure we placed all characters
            if (charIndex < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${charIndex} empty cells, need ${wordLength}`);
            }

            // Create word data: text = display form (e.g., "run on"); positions use normalizedWord
            const displayText = wordToPlaceDisplay || normalizedWord;
            const wordData = {
                word: displayText,
                text: displayText,
                positions: positions,
                direction: 'scattered',
                length: normalizedWord.length,
                startRow: positions[0]?.row || 0,
                startCol: positions[0]?.col || 0
            };

            // Add the word to the puzzle and wait for it to complete
            if (onWordAdd) {
                try {
                    // Call onWordAdd and await if it returns a promise
                    const addResult = onWordAdd(wordData);
                    if (addResult && typeof addResult.then === 'function') {
                        await addResult;
                    } else {
                        // Give a small delay to ensure the word is processed if not async
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                    
                    // Update puzzle type to HIDDEN_MESSAGE when word is placed, including title and hiddenMessage
                    if (gridTpl?.id) {
                        try {
                            const currentState = gridTpl.state || DRAFT;
                            const titleToUse = updatedTitle !== undefined ? updatedTitle : (gridTpl.title || '');
                            // Use the normalized word (lowercase) as the hidden message
                            await AdminGridBoardService.updateHwTplState(gridTpl.id, currentState, 'HIDDEN_MESSAGE', null, titleToUse, normalizedWord);
                            
                            // Trigger refresh to update the puzzle data with the new type and hiddenMessage
                            if (onRefresh) {
                                onRefresh();
                            }
                            
                            onShowSnackbar(`Word "${wordToPlace}" placed in puzzle. Puzzle type set to HIDDEN_MESSAGE.`, "success");
                        } catch (error) {
                            Logger.error("Error updating puzzle type to HIDDEN_MESSAGE:", error);
                            onShowSnackbar(`Word "${wordToPlace}" placed in puzzle, but failed to update puzzle type`, "warning");
                        }
                    } else {
                        onShowSnackbar(`Word "${wordToPlace}" placed in puzzle`, "success");
                    }
                } catch (error) {
                    Logger.error("Error adding word to puzzle:", error);
                    onShowSnackbar("Error adding word to puzzle: " + error.message, "error");
                }
            }

            // Reset form and candidates after successful add
            setManualPattern('');
            setManualRow(0);
            setManualCol(0);
            setManualDirection(DIRECTION_HORIZONTAL);
            setLeftToRight(true);
            setManualCandidates([]);

        } catch (error) {
            Logger.error("Error placing word in puzzle:", error);
            onShowSnackbar("Error placing word in puzzle: " + error.message, "error");
        } finally {
            setConfirmDialogOpen(false);
            setWordToPlace(null);
            setWordToPlaceDisplay(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, height: '100%', flex: 1 }}>
            {/* Manual Pattern Input Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Pattern"
                        value={manualPattern}
                        onChange={(e) => setManualPattern(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && manualPattern.trim() && manualPattern.trim().length > 3 && !manualLoading) {
                                handleManualPatternSearch();
                            } else if (e.key === 'Enter' && manualPattern.trim().length <= 3) {
                                onShowSnackbar("Pattern must be longer than 3 characters", "error");
                            }
                        }}
                        placeholder="e.g., ?AT??N or CAT__G"
                        helperText={manualPattern.trim().length > 0 && manualPattern.trim().length <= 3 
                            ? "Pattern must be longer than 3 characters" 
                            : "Use ? for empty cells and letters for existing ones (min 4 characters)"}
                        error={manualPattern.trim().length > 0 && manualPattern.trim().length <= 3}
                        size="small"
                        sx={{ flex: 1 }}
                    />
                    <Tooltip title={manualLoading ? 'Searching...' : 'Find Words'}>
                        <span>
                            <IconButton
                                onClick={handleManualPatternSearch}
                                disabled={!manualPattern.trim() || manualLoading}
                                color="primary"
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    '&:disabled': {
                                        borderColor: 'action.disabled'
                                    }
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                {manualDirection !== 'scattered' && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <TextField
                            label="Row"
                            type="number"
                            value={manualRow}
                            onChange={(e) => handleManualRowChange(e.target.value)}
                            inputProps={{ min: 0, max: gridSize - 1 }}
                            size="small"
                            sx={{ width: 80 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                            <TextField
                                label="Column"
                                type="number"
                                value={manualCol}
                                onChange={(e) => handleManualColChange(e.target.value)}
                                inputProps={{ min: 0, max: gridSize - 1 }}
                                size="small"
                                sx={{ width: 80 }}
                            />
                            <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                    fontWeight: 'bold',
                                    letterSpacing: 0.5,
                                    fontSize: '0.75rem',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: (theme) => theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                    border: (theme) => `1px solid ${theme.palette.error.main}`,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Empty: {emptyCellsCount}
                            </Typography>
                        </Box>
                    </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Direction:
                    </Typography>
                    <RadioGroup
                        row
                        value={manualDirection}
                        onChange={(e) => handleDirectionChange(e.target.value)}
                    >
                        <Tooltip title="Horizontal">
                            <FormControlLabel
                                value={DIRECTION_HORIZONTAL}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveHorizontal /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Vertical">
                            <FormControlLabel
                                value={DIRECTION_VERTICAL}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveVertical /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Diagonal Right Up">
                            <FormControlLabel
                                value={DIRECTION_DIAGONAL_RIGHT_UP}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveDiagonal /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Diagonal Left Up">
                            <FormControlLabel
                                value={DIRECTION_DIAGONAL_LEFT_UP}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveDiagonal2 /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Scattered Placement">
                            <FormControlLabel
                                value="scattered"
                                control={<Radio size="small" />}
                                label="Scattered"
                            />
                        </Tooltip>
                    </RadioGroup>
                    {manualDirection !== 'scattered' && (
                        <ToggleButtonGroup
                            value={leftToRight ? "leftToRight" : "rightToLeft"}
                            exclusive
                            onChange={(e, newValue) => {
                                if (newValue !== null) {
                                    handleLeftToRightChange(newValue === "leftToRight");
                                }
                            }}
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            <ToggleButton value="leftToRight">
                                {manualDirection === DIRECTION_HORIZONTAL ? "Left to Right" :
                                 manualDirection === DIRECTION_VERTICAL ? "Top to Bottom" :
                                 manualDirection === DIRECTION_DIAGONAL_RIGHT_UP ? "Top-Right to Bottom-Left" :
                                 manualDirection === DIRECTION_DIAGONAL_LEFT_UP ? "Top-Left to Bottom-Right" :
                                 "Forward"}
                            </ToggleButton>
                            <ToggleButton value="rightToLeft">
                                {manualDirection === DIRECTION_HORIZONTAL ? "Right to Left" :
                                 manualDirection === DIRECTION_VERTICAL ? "Bottom to Top" :
                                 manualDirection === DIRECTION_DIAGONAL_RIGHT_UP ? "Bottom-Left to Top-Right" :
                                 manualDirection === DIRECTION_DIAGONAL_LEFT_UP ? "Bottom-Right to Top-Left" :
                                 "Reverse"}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {manualDirection === 'scattered' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {getDirectionLabel()}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Manual Candidates Section */}
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                        Word Candidates
                    </Typography>
                    {memoizedCandidates.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Previous Page">
                                <span>
                                    <IconButton
                                        onClick={handlePreviousPage}
                                        disabled={currentPage === 1}
                                        size="small"
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                                {currentPage} / {totalPages}
                            </Typography>
                            <Tooltip title="Next Page">
                                <span>
                                    <IconButton
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                        size="small"
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                {manualLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Typography>Searching for words...</Typography>
                    </Box>
                ) : (
                    <List sx={{ flex: 1, overflow: 'auto' }}>
                        {memoizedCandidates.length === 0 ? (
                            <ListItem>
                                <ListItemText
                                    primary="No candidates found"
                                    secondary="Enter a pattern and click 'Find Words' to search"
                                />
                            </ListItem>
                        ) : (
                            paginatedCandidates.map((candidate) => (
                                <GridCandEntry
                                    key={candidate.key}
                                    candidate={candidate}
                                    isSelected={selectedManualWord?.word === candidate.word}
                                    onWordSelect={onWordSelect}
                                    onWordAdd={handleManualWordAdd}
                                    mode="manual"
                                />
                            ))
                        )}
                    </List>
                )}
            </Box>

            {/* Alert Dialog */}
            <AlertDialog
                open={alertOpen}
                setOpen={setAlertOpen}
                title="Word Placement Error"
                message={alertMessage}
            />

            {/* Confirmation Dialog for Scattered Placement */}
            <GirdCanAssocConfirmDialog
                open={confirmDialogOpen}
                setOpen={setConfirmDialogOpen}
                word={wordToPlace}
                title={gridTpl?.title || ''}
                gridTplId={gridTpl?.id}
                currentState={gridTpl?.state || DRAFT}
                puzzleType="HIDDEN_MESSAGE"
                onConfirm={handleConfirmWordPlacement}
                language={gridTpl?.language || 'english'}
            />
        </Box>
    );
}

